type DeployStage = 'dev' | 'prod';

const resolveEnvValue = (key: string): string | undefined => {
  const processEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  if (processEnv?.[key]) {
    return processEnv[key];
  }

  const metaEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return metaEnv?.[key];
};

const normalizeBaseUrl = (value: string): string => value.replace(/\/+$/, '');

const rawStage = (resolveEnvValue('VITE_HAI_STAGE') ?? resolveEnvValue('HAI_STAGE') ?? 'prod').toLowerCase();
export const MARKETING_STAGE: DeployStage = rawStage === 'dev' ? 'dev' : 'prod';
export const MARKETING_IS_PROD = MARKETING_STAGE === 'prod';
export const MARKETING_BASE_URL = normalizeBaseUrl(
  resolveEnvValue('VITE_MARKETING_BASE_URL') ?? resolveEnvValue('HAI_MKT_BASE_URL') ?? 'https://heyea.ai'
);

export interface StructuredData {
  readonly [key: string]: unknown;
}

export interface SeoMetadata {
  readonly title: string;
  readonly description: string;
  readonly canonicalPath: string;
  readonly ogType?: 'website' | 'article';
  readonly socialImagePath?: string;
  readonly socialImageAlt?: string;
  readonly twitterSite?: string;
  readonly jsonLd: StructuredData | readonly StructuredData[];
}

export interface PageSection {
  readonly heading: string;
  readonly paragraphs: readonly string[];
  readonly bullets?: readonly string[];
}

export interface CtaBlock {
  readonly heading: string;
  readonly body: string;
  readonly primaryLabel: string;
  readonly primaryHref: string;
  readonly secondaryLabel?: string;
  readonly secondaryHref?: string;
}

export interface MarketingPage {
  readonly id: MarketingPageId;
  readonly path: string;
  readonly navLabel: string;
  readonly heroEyebrow: string;
  readonly heroTitle: string;
  readonly heroDescription: string;
  readonly heroPoints: readonly string[];
  readonly sections: readonly PageSection[];
  readonly cta: CtaBlock;
  readonly seo: SeoMetadata;
}

export type MarketingPageId =
  | 'home'
  | 'pricing'
  | 'lp-clockwise-alternative'
  | 'feature-true-availability'
  | 'feature-calendar-ui'
  | 'feature-meeting-polling'
  | 'feature-smart-booking'
  | 'roadmap'
  | 'solution-consultants'
  | 'solution-sales'
  | 'about'
  | 'contact'
  | 'privacy-policy'
  | 'terms-of-service';

const asUrl = (path: string): string => `${MARKETING_BASE_URL}${path}`;

interface SocialImageEntry {
  readonly path: string;
  readonly alt: string;
}

const DEFAULT_SOCIAL_IMAGE: SocialImageEntry = {
  path: '/assets/16dd274f662e10a1732f13b180cb582b657a084e-w1280.webp',
  alt: 'Heyea True Availability scheduling preview'
};

const SOCIAL_IMAGE_BY_ROUTE: Readonly<Record<string, SocialImageEntry>> = {
  '/': DEFAULT_SOCIAL_IMAGE,
  '/pricing': {
    path: '/assets/02cd12a9e465798a200eca19fcfb1eae33742237-w1280.webp',
    alt: 'Heyea booking pages preview'
  },
  '/lp/clockwise-alternative': {
    path: '/assets/67d4eaa3d207ebc83a4494f699d8359598b20c48-w1280.webp',
    alt: 'Heyea canonical timeline for former Clockwise users'
  },
  '/features/true-availability': DEFAULT_SOCIAL_IMAGE,
  '/features/calendar-ui': {
    path: '/assets/67d4eaa3d207ebc83a4494f699d8359598b20c48-w1280.webp',
    alt: 'Unified calendar and privacy controls in Heyea'
  },
  '/features/meeting-polling': {
    path: '/assets/d4375c61431f5dd7a58dadda5b5ca40aa484e7e2-w1280.webp',
    alt: 'Heyea event polling workflow'
  },
  '/features/smart-booking': {
    path: '/assets/02cd12a9e465798a200eca19fcfb1eae33742237-w1280.webp',
    alt: 'Smart appointment booking in Heyea'
  },
  '/roadmap': {
    path: '/assets/033ed3f8d872183727d430d19cfbcce88604077c-w1280.webp',
    alt: 'Heyea product roadmap preview'
  },
  '/solutions/independent-consultants': {
    path: '/assets/02cd12a9e465798a200eca19fcfb1eae33742237-w1280.webp',
    alt: 'Heyea scheduling for independent consultants'
  },
  '/solutions/sales-teams': {
    path: '/assets/d4375c61431f5dd7a58dadda5b5ca40aa484e7e2-w1280.webp',
    alt: 'Heyea scheduling for sales teams'
  },
  '/about': {
    path: '/assets/7a15b717b92f2a0adb485677e847fece4de33cba.webp',
    alt: 'Heyea company logo'
  },
  '/contact': {
    path: '/assets/67d4eaa3d207ebc83a4494f699d8359598b20c48-w1280.webp',
    alt: 'Contact Heyea for support and sales'
  },
  '/privacy-policy': {
    path: '/assets/7a15b717b92f2a0adb485677e847fece4de33cba.webp',
    alt: 'Heyea privacy policy'
  },
  '/terms-of-service': {
    path: '/assets/7a15b717b92f2a0adb485677e847fece4de33cba.webp',
    alt: 'Heyea terms of service'
  }
};

const baseJsonLd = (name: string, description: string, path: string): StructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url: asUrl(path)
});

export const MARKETING_PAGES: Readonly<Record<MarketingPageId, MarketingPage>> = {
  home: {
    id: 'home',
    path: '/',
    navLabel: 'Home',
    heroEyebrow: 'True Availability',
    heroTitle: 'More bookable time without sacrificing privacy.',
    heroDescription:
      'Stop choosing between privacy and profit. Heyea uses AI to turn all your calendars into one smart schedule, so you can safely offer time that could work, not just the time that looks empty.',
    heroPoints: [
      '6x more bookable time without exposing your calendar',
      'Traditional booking tools only show empty time. Heyea reveals intelligent availability.',
      'Event details never exposed. Only impact classification is shared.'
    ],
    sections: [],
    cta: {
      heading: 'Ready to master your time?',
      body: "Join 50,000+ professionals who've transformed their scheduling with Heyea.",
      primaryLabel: 'Get Started Free',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'Book a Demo',
      secondaryHref: '/contact'
    },
    seo: {
      title: 'Heyea | More Bookable Time Without Sacrificing Privacy',
      description:
        'Heyea uses True Availability to help you share realistic bookable time across Google and Outlook without exposing private calendar details.',
      canonicalPath: '/',
      ogType: 'website',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Heyea',
          url: MARKETING_BASE_URL
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Heyea',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description:
            'Calendar scheduling software with True Availability, booking pages, and event polling for external coordination.',
          // Aggregate rating is intentionally omitted until policy-compliant first-party rating data is available.
          offers: {
            '@type': 'Offer',
            url: asUrl('/pricing'),
            price: '8',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '8',
              priceCurrency: 'USD',
              billingIncrement: 1,
              unitText: 'month'
            }
          }
        }
      ]
    }
  },
  pricing: {
    id: 'pricing',
    path: '/pricing',
    navLabel: 'Pricing',
    heroEyebrow: 'How Heyea compares',
    heroTitle: 'Who says you can’t buy time?',
    heroDescription: 'True Availability, intelligent scheduling, and real control over your time.',
    heroPoints: [
      'True Availability across Google and Outlook',
      'Appointment booking using True Availability',
      'Event polling and privacy-respecting availability sharing'
    ],
    sections: [],
    cta: {
      heading: 'See your real availability',
      body: 'Stop guessing. Start scheduling with intent.',
      primaryLabel: 'Connect your calendar',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'View a demo',
      secondaryHref: '/contact'
    },
    seo: {
      title: 'Heyea Pricing | True Availability Plans',
      description:
        'Compare Heyea plans for True Availability, appointment booking, and event polling across Google and Outlook calendars.',
      canonicalPath: '/pricing',
      jsonLd: baseJsonLd('Heyea Pricing', 'Pricing plans for True Availability scheduling.', '/pricing')
    }
  },
  'lp-clockwise-alternative': {
    id: 'lp-clockwise-alternative',
    path: '/lp/clockwise-alternative',
    navLabel: 'Clockwise Alternative',
    heroEyebrow: 'Clockwise Alternative',
    heroTitle: "Clockwise is Closing. Your Focus Time Shouldn't.",
    heroDescription:
      'Transition to Heyea in under 5 minutes to protect your AI-optimized schedule, preserve privacy, and unlock 6x more bookable time.',
    heroPoints: [
      '14-day free trial with concierge migration help',
      'Protect private details while exposing flexible time',
      'Connect Google, Outlook, and 20+ calendar systems'
    ],
    sections: [],
    cta: {
      heading: 'Keep your focus time working',
      body: 'Start free, keep your schedule stable, and talk to a migration specialist when you need help.',
      primaryLabel: 'Save My Schedule - Start Free Trial',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'Talk to a Migration Specialist',
      secondaryHref: '/contact'
    },
    seo: {
      title: 'Clockwise Alternative: Stable AI Focus Time Scheduling | Heyea',
      description:
        'Looking for a Clockwise alternative? Heyea offers True Availability, privacy-first scheduling, and a stable migration path for teams leaving Clockwise.',
      canonicalPath: '/lp/clockwise-alternative',
      jsonLd: [
        baseJsonLd(
          'Clockwise Alternative',
          'A privacy-first AI scheduling alternative for teams migrating off Clockwise.',
          '/lp/clockwise-alternative'
        ),
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What makes Heyea a strong Clockwise alternative?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Heyea combines True Availability, privacy-first scheduling, and a canonical timeline across Google, Outlook, and more than 20 calendar systems.'
              }
            },
            {
              '@type': 'Question',
              name: 'Can my team migrate from Clockwise without a long setup?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. Teams can start with a free trial, connect calendars, map focus time into flexible or protected availability, and work with a migration specialist when needed.'
              }
            },
            {
              '@type': 'Question',
              name: 'Does Heyea offer a credit match for active Clockwise subscriptions?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. Active Clockwise customers can submit their final receipt during onboarding, and Heyea will review the remaining balance for a matching credit on an eligible plan.'
              }
            }
          ]
        }
      ]
    }
  },
  'feature-true-availability': {
    id: 'feature-true-availability',
    path: '/features/true-availability',
    navLabel: 'True Availability',
    heroEyebrow: 'Core Feature',
    heroTitle: 'True Availability',
    heroDescription: 'One timeline that represents you, privately.',
    heroPoints: [
      'Most tools show free or busy. We show what time could work, without showing your events.',
      'Go beyond free or busy with intelligent availability states.',
      'You see everything. The world sees only what you choose.'
    ],
    sections: [],
    cta: {
      heading: 'Start Free Trial',
      body: 'Get True Availability running on your calendars in minutes.',
      primaryLabel: 'Start Free Trial',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'View Pricing',
      secondaryHref: '/pricing'
    },
    seo: {
      title: 'True Availability | Heyea',
      description:
        'See how Heyea computes True Availability so you can share realistic bookable time without exposing private event details.',
      canonicalPath: '/features/true-availability',
      jsonLd: baseJsonLd(
        'True Availability',
        'The core Heyea feature for privacy-preserving schedule intelligence.',
        '/features/true-availability'
      )
    }
  },
  'feature-calendar-ui': {
    id: 'feature-calendar-ui',
    path: '/features/calendar-ui',
    navLabel: 'Calendar UI',
    heroEyebrow: 'The Unified Calendar',
    heroTitle: 'All Your Calendars. One Place.',
    heroDescription: 'See and edit all your events across all calendar systems in one UI.',
    heroPoints: [
      'Bi-directional power. Edit any event from our system. All your time in one place.',
      'Find any event across all connected accounts instantly.',
      'Personal events show as Free, Flexible, Disruptive, or Protected to others while remaining fully detailed for you.'
    ],
    sections: [],
    cta: {
      heading: 'One Timeline. Zero Conflicts.',
      body: 'See and edit all your events across all calendar systems in one UI.',
      primaryLabel: 'Start Free Trial',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'Explore True Availability',
      secondaryHref: '/features/true-availability'
    },
    seo: {
      title: 'Unified Calendar UI | Heyea',
      description:
        'Use a single calendar interface across Google and Outlook with conflict visibility, privacy masks, and fast edits.',
      canonicalPath: '/features/calendar-ui',
      jsonLd: baseJsonLd('Unified Calendar UI', 'A single UI for all calendar systems.', '/features/calendar-ui')
    }
  },
  'feature-meeting-polling': {
    id: 'feature-meeting-polling',
    path: '/features/meeting-polling',
    navLabel: 'Event Polling',
    heroEyebrow: 'Event Polling',
    heroTitle: 'Find the best time for everyone.',
    heroDescription: 'Turn scattered calendars and private schedules into a clear way to pick a time.',
    heroPoints: [
      'Propose time slots based on actual availability, not empty calendar gaps.',
      'Participants vote for or against times without exposing calendar data.',
      'Organizer sees definitive votes and finalizes with clear signal.'
    ],
    sections: [],
    cta: {
      heading: 'Turn thread chaos into decision signal',
      body: 'Use Event Polling powered by True Availability to find a workable time faster.',
      primaryLabel: 'Start Free Trial',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'See True Availability',
      secondaryHref: '/features/true-availability'
    },
    seo: {
      title: 'Event Polling | Heyea',
      description:
        'Run meeting polls with True Availability so participants vote on realistic options without sharing private calendar details.',
      canonicalPath: '/features/meeting-polling',
      jsonLd: baseJsonLd(
        'Event Polling',
        'Coordinate groups with vote-based scheduling using availability signals.',
        '/features/meeting-polling'
      )
    }
  },
  'feature-smart-booking': {
    id: 'feature-smart-booking',
    path: '/features/smart-booking',
    navLabel: 'Smart Booking',
    heroEyebrow: 'Smart Appointment Booking',
    heroTitle: 'Book the easy slots. Request the hard ones.',
    heroDescription:
      'Booking pages that use True Availability so invitees can book realistic time without seeing calendar details.',
    heroPoints: [
      'Book now for low-cost windows',
      'Request-only guardrails for expensive time',
      'Privacy-preserving availability sharing'
    ],
    sections: [],
    cta: {
      heading: 'Start Free Trial',
      body: 'Use Smart Appointment Booking with True Availability and keep control over every request.',
      primaryLabel: 'Start Free Trial',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'View Pricing',
      secondaryHref: '/pricing'
    },
    seo: {
      title: 'Smart Appointment Booking | Heyea',
      description:
        'Use True Availability booking pages so people can book easy time, request hard slots, and never see your calendar details.',
      canonicalPath: '/features/smart-booking',
      jsonLd: baseJsonLd('Smart Appointment Booking', 'Rule-based booking using True Availability.', '/features/smart-booking')
    }
  },
  roadmap: {
    id: 'roadmap',
    path: '/roadmap',
    navLabel: 'Roadmap',
    heroEyebrow: 'Where Heyea Is Going Next',
    heroTitle: 'Where Heyea Is Going Next',
    heroDescription:
      'We are building smarter ways to expand revenue-generating availability while preserving your privacy and control.',
    heroPoints: [
      'Passive Billable Hour Estimates',
      'Custom AI True Availability Assessor',
      'Time Usage Analytics'
    ],
    sections: [],
    cta: {
      heading: 'Help Shape What Comes Next',
      body: 'Have input on these features? Tell us what would make them indispensable.',
      primaryLabel: 'Share Feedback',
      primaryHref: '/contact',
      secondaryLabel: 'Start Free Trial',
      secondaryHref: 'https://app.heyea.ai/auth/signup'
    },
    seo: {
      title: 'Heyea Roadmap',
      description:
        'See what Heyea has recently shipped, what is in progress, and what is coming next for True Availability scheduling.',
      canonicalPath: '/roadmap',
      jsonLd: baseJsonLd('Heyea Roadmap', 'Planned and in-progress features for Heyea.', '/roadmap')
    }
  },
  'solution-consultants': {
    id: 'solution-consultants',
    path: '/solutions/independent-consultants',
    navLabel: 'For Independent Consultants',
    heroEyebrow: 'For Independent Consultants',
    heroTitle: 'Let clients book time you can actually deliver.',
    heroDescription:
      'Heyea figures out your real availability across your Google and Outlook calendars and your subcontractors, then powers booking links and polls with a True Availability heatmap.',
    heroPoints: [
      'Works with Google Calendar and Outlook',
      'Event details stay private',
      'Team availability supported'
    ],
    sections: [],
    cta: {
      heading: 'Connect your calendars in minutes',
      body: 'Stop losing billable hours to scheduling. Let Heyea compute your True Availability and protect your time.',
      primaryLabel: 'Connect your calendars',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'View a demo booking page',
      secondaryHref: '/features/smart-booking'
    },
    seo: {
      title: 'For Independent Consultants | Heyea',
      description:
        'Help clients book workable times while protecting private details with True Availability, booking pages, and event polls.',
      canonicalPath: '/solutions/independent-consultants',
      jsonLd: baseJsonLd(
        'Heyea for Independent Consultants',
        'Scheduling workflows for consultants and subcontractor coordination.',
        '/solutions/independent-consultants'
      )
    }
  },
  'solution-sales': {
    id: 'solution-sales',
    path: '/solutions/sales-teams',
    navLabel: 'For Sales Teams',
    heroEyebrow: 'For Sales Teams',
    heroTitle: 'Let prospects request times that might work, not just empty slots.',
    heroDescription:
      'Heyea merges Google and Outlook calendars, computes True Availability, and powers customizable booking pages and polls.',
    heroPoints: [
      'Google Calendar + Outlook',
      'Event details stay private',
      'Built for 1:1 and group scheduling'
    ],
    sections: [],
    cta: {
      heading: 'Ready to book meetings faster?',
      body: 'Let Heyea compute your True Availability so your team can focus on selling, not scheduling.',
      primaryLabel: 'Start a trial',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'See how it works',
      secondaryHref: '/features/true-availability'
    },
    seo: {
      title: 'For Sales Teams | Heyea',
      description:
        'Give prospects safer options with True Availability booking pages and event polls, without exposing private calendar details.',
      canonicalPath: '/solutions/sales-teams',
      jsonLd: baseJsonLd('Heyea for Sales Teams', 'Sales scheduling with request-a-time and availability heatmaps.', '/solutions/sales-teams')
    }
  },
  about: {
    id: 'about',
    path: '/about',
    navLabel: 'About',
    heroEyebrow: 'American-Made • Veteran-Owned',
    heroTitle: 'About Us',
    heroDescription: 'Built here. Run here. Answerable here.',
    heroPoints: [
      'Heyea is a Veteran-Owned, American software company, founded and built in Austin, Texas.',
      'We hire Americans. We build with Americans. We answer to Americans.',
      'We do not outsource trust.'
    ],
    sections: [],
    cta: {
      heading: 'Start Your Free Trial',
      body: '14-day free trial • No credit card required.',
      primaryLabel: 'Start Free Trial',
      primaryHref: 'https://app.heyea.ai/auth/signup',
      secondaryLabel: 'Contact Sales',
      secondaryHref: '/contact'
    },
    seo: {
      title: 'About Heyea',
      description:
        'Learn about Heyea, an American-made, veteran-owned software company built in Austin, Texas.',
      canonicalPath: '/about',
      jsonLd: baseJsonLd('About Heyea', 'Company background and principles.', '/about')
    }
  },
  contact: {
    id: 'contact',
    path: '/contact',
    navLabel: 'Contact',
    heroEyebrow: 'Contact',
    heroTitle: 'Contact Heyea',
    heroDescription:
      'Need legal guidance, enterprise support, or product troubleshooting? Reach the right team directly.',
    heroPoints: [
      'Policies and legal requests',
      'Enterprise contracts, integrations, and sales',
      'Bug reports and technical problems'
    ],
    sections: [
      {
        heading: 'Right inbox, faster response',
        paragraphs: [
          'Use our contact page to route your request to the right team so we can respond with the right context quickly.'
        ],
        bullets: [
          'Policies and legal inquiries',
          'Sales and enterprise requests',
          'Bug reports and technical issues'
        ]
      }
    ],
    cta: {
      heading: 'Open the contact page',
      body: 'Choose the request type and email the right team.',
      primaryLabel: 'Go to Contact',
      primaryHref: '/contact',
      secondaryLabel: 'Start Free Trial',
      secondaryHref: 'https://app.heyea.ai/auth/signup'
    },
    seo: {
      title: 'Contact Heyea',
      description:
        'Contact Heyea for legal and privacy questions, enterprise contracts and integrations, or technical issue reporting.',
      canonicalPath: '/contact',
      jsonLd: baseJsonLd('Contact Heyea', 'Contact channels for legal, sales, and technical support.', '/contact')
    }
  },
  'privacy-policy': {
    id: 'privacy-policy',
    path: '/privacy-policy',
    navLabel: 'Privacy Policy',
    heroEyebrow: 'Privacy Policy',
    heroTitle: 'Privacy Policy',
    heroDescription:
      'In this policy, we lay out what data we collect and why, how your data is handled, and your rights with respect to your data.',
    heroPoints: ['Last Updated: May 18, 2025', 'We never sell your data.', 'This policy applies to all products built and maintained by Heyea, LLC.'],
    sections: [],
    cta: {
      heading: 'Questions about this policy?',
      body: 'Contact our support team if you need clarification on how your data is handled.',
      primaryLabel: 'Contact Support',
      primaryHref: '/contact',
      secondaryLabel: 'Terms of Service',
      secondaryHref: '/terms-of-service'
    },
    seo: {
      title: 'Heyea Privacy Policy',
      description: 'Read the Heyea Privacy Policy and how we collect, process, and protect your data.',
      canonicalPath: '/privacy-policy',
      jsonLd: baseJsonLd('Privacy Policy', 'Heyea data privacy and processing terms.', '/privacy-policy')
    }
  },
  'terms-of-service': {
    id: 'terms-of-service',
    path: '/terms-of-service',
    navLabel: 'Terms of Service',
    heroEyebrow: 'Terms of Service',
    heroTitle: 'Terms of Service',
    heroDescription:
      'When you use any Heyea products or services, you are agreeing to these latest Terms of Service.',
    heroPoints: [
      'Last Updated: June 4, 2025',
      'Violation of these terms may result in account termination.',
      'We may update these Terms of Service at any time.'
    ],
    sections: [],
    cta: {
      heading: 'Need clarification on these terms?',
      body: 'If you have a question about any of these Terms, contact our support team.',
      primaryLabel: 'Contact Support',
      primaryHref: '/contact',
      secondaryLabel: 'Privacy Policy',
      secondaryHref: '/privacy-policy'
    },
    seo: {
      title: 'Heyea Terms of Service',
      description: 'Read the Heyea Terms of Service for account usage, billing, restricted usage, and legal terms.',
      canonicalPath: '/terms-of-service',
      jsonLd: baseJsonLd('Terms of Service', 'Heyea service terms and legal conditions.', '/terms-of-service')
    }
  }
};

export const MARKETING_PAGE_ORDER: readonly MarketingPageId[] = [
  'home',
  'pricing',
  'lp-clockwise-alternative',
  'feature-true-availability',
  'feature-calendar-ui',
  'feature-meeting-polling',
  'feature-smart-booking',
  'roadmap',
  'solution-consultants',
  'solution-sales',
  'about',
  'contact',
  'privacy-policy',
  'terms-of-service'
] as const;

export const PRERENDER_ROUTES: readonly string[] = MARKETING_PAGE_ORDER.map((id) => MARKETING_PAGES[id].path);

export interface NavItem {
  readonly label: string;
  readonly path: string;
}

export const FEATURE_NAV_ITEMS: readonly NavItem[] = [
  { label: 'True Availability', path: '/features/true-availability' },
  { label: 'Calendar UI', path: '/features/calendar-ui' },
  { label: 'Event Polling', path: '/features/meeting-polling' },
  { label: 'Smart Booking', path: '/features/smart-booking' },
  { label: 'Product Roadmap', path: '/roadmap' }
] as const;

export const SOLUTION_NAV_ITEMS: readonly NavItem[] = [
  { label: 'For Independent Consultants', path: '/solutions/independent-consultants' },
  { label: 'For Sales Teams', path: '/solutions/sales-teams' }
] as const;

export const PRIMARY_NAV_ITEMS: readonly NavItem[] = [
  { label: 'Pricing', path: '/pricing' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' }
] as const;

export const MARKETING_PAGES_BY_PATH: Readonly<Record<string, MarketingPage>> = Object.fromEntries(
  Object.values(MARKETING_PAGES).map((page) => [page.path, page])
);

export const getPageById = (pageId: MarketingPageId): MarketingPage => MARKETING_PAGES[pageId];

export const getPageByPath = (path: string): MarketingPage | undefined => MARKETING_PAGES_BY_PATH[path];

export const getCanonicalUrl = (canonicalPath: string): string => asUrl(canonicalPath);

export const getSocialImageByPath = (canonicalPath: string): SocialImageEntry =>
  SOCIAL_IMAGE_BY_ROUTE[canonicalPath] ?? DEFAULT_SOCIAL_IMAGE;

export const toAbsoluteMarketingUrl = (pathOrUrl: string): string =>
  pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://') ? pathOrUrl : asUrl(pathOrUrl);
